import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import UssdSettingSection from './components/Settings/UssdSettingSection';
import UssdUnactivatedState from './components/Settings/UssdUnactivatedState';
import UssdSuspendedState from './components/Settings/UssdSuspendedState';
import {
  getSubscriptions,
  getProducts,
  initializePaystackPayment,
} from '../api/subscriptions.api';

export default function UssdPage() {
  const { activeApp: reduxActiveApp } = useSelector((state) => state.auth);
  let activeApp = reduxActiveApp;
  if (!activeApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        activeApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          activeApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore
    }
  }

  const [subscriptions, setSubscriptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentAppId = activeApp?._id;

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [subRes, prodRes] = await Promise.all([
          getSubscriptions(),
          getProducts(),
        ]);
        if (subRes && subRes.ok && subRes.data?.data && isMounted) {
          setSubscriptions(subRes.data.data);
        } else if (subRes && Array.isArray(subRes.data) && isMounted) {
          setSubscriptions(subRes.data);
        }
        if (prodRes && prodRes.ok && prodRes.data?.data && isMounted) {
          setProducts(prodRes.data.data);
        } else if (prodRes && Array.isArray(prodRes.data) && isMounted) {
          setProducts(prodRes.data);
        }
      } catch (e) {
        console.error('Error fetching USSD subscriptions:', e);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [currentAppId]);

  const appSubs = currentAppId
    ? subscriptions.filter(
        (s) => s.app?._id === currentAppId || s.app === currentAppId
      )
    : subscriptions;

  const ussdProduct = products.find(
    (p) =>
      p.type === 'ussd' ||
      (typeof p.name === 'string' && p.name.toLowerCase().includes('ussd'))
  );

  const ussdSub = appSubs.find(
    (s) =>
      s.product?.type === 'ussd' ||
      (typeof s.product === 'object' && s.product?.name?.toLowerCase().includes('ussd')) ||
      (ussdProduct && (s.product === ussdProduct._id || s.product === ussdProduct.id))
  );

  const isSubActive = ussdSub?.status === 'active';
  const isSubSuspended =
    ussdSub?.status === 'suspended' ||
    ussdSub?.status === 'past-due' ||
    ussdSub?.status === 'past_due' ||
    ussdSub?.status === 'paused';

  const handleReactivateUssd = async () => {
    if (!ussdSub?._id) {
      toast.error('USSD subscription record not found.');
      return;
    }

    try {
      toast.info('Connecting to Paystack checkout portal...');
      const initRes = await initializePaystackPayment(ussdSub._id, {
        months: 1,
        callbackUrl: window.location.href,
      });

      if (initRes && initRes.ok && initRes.data?.data?.authorization_url) {
        window.location.href = initRes.data.data.authorization_url;
        return;
      }

      toast.error(initRes?.data?.message || 'Failed to initialize Paystack checkout.');
    } catch (e) {
      console.error('Error renewing USSD subscription:', e);
      toast.error('Failed to connect to Paystack payment gateway.');
      throw e;
    }
  };

  // LOADING STATE
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 100px)',
          bgcolor: '#FFFFFF',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress size={32} sx={{ color: '#0071E3' }} />
      </Box>
    );
  }

  // STATE: PAUSED (AFTER GRACE PERIOD)
  if (isSubSuspended) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 100px)',
          bgcolor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2.5, sm: 4 },
          py: { xs: 6, sm: 8 },
        }}
      >
        <UssdSuspendedState
          subscription={ussdSub}
          onReactivate={handleReactivateUssd}
        />
      </Box>
    );
  }

  // STATE: UNACTIVATED / NOT ACTIVE (MINIMAL APPLE STATE)
  if (!isSubActive) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 100px)',
          bgcolor: '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2.5, sm: 4 },
          py: { xs: 6, sm: 8 },
        }}
      >
        <UssdUnactivatedState />
      </Box>
    );
  }

  // STATE: ACTIVE (OPERATIONAL SETTINGS)
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FBFBFD', py: { xs: 2.5, sm: 3.5 } }}>
      <Container maxWidth="lg">
        <UssdSettingSection subscription={ussdSub} activeApp={activeApp} />
      </Container>
    </Box>
  );
}
